import axios, {
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
} from "axios";
import { AppSettingsService } from "@/service/AppSettingsService";
import Logger from "@/utils/Logger";
import store from "@/store";
import {
  selectAccessToken,
  selectIsAuthenticated,
} from "@/store/selectors/authSelectors";

// Determine if the application is running on localhost
const isLocalhost = window.location.hostname.includes("localhost");

// Configuration constants
const API_CONFIG = {
  BASE_URL: AppSettingsService.getApiUrl(),
  HEADERS: {
    CACHE_CONTROL: "no-cache",
    PRAGMA: "no-cache",
    CONTENT_TYPE: "application/json",
    CLIENT_IDENTIFIER: AppSettingsService.getApiClientIdentifier(),
  },
  TIMEOUT: 10000, // 10 seconds
};

/**
 * Get access token from Redux store (safe for interceptors - no hooks)
 */
const getAccessToken = (): string | null => {
  try {
    const state = store.getState();
    const token = selectAccessToken(state);
    const isAuthenticated = selectIsAuthenticated(state);

    if (token && isAuthenticated) {
      return token;
    }
    return null;
  } catch (error) {
    Logger.error("Error getting access token from store", error, ["ApiClient"]);
    return null;
  }
};

/**
 * Network error handler
 */
const handleNetworkError = (error: AxiosError, context: string): void => {
  if (axios.isAxiosError(error) && error.code === "ERR_NETWORK") {
    Logger.error(
      `Network error in ${context}, redirecting to connection-error page`,
      error,
      ["ApiClient"],
    );

    // Prevent multiple redirects by checking current location
    if (!window.location.pathname.includes("/connection-error")) {
      // Use replace to avoid back button issues
      window.location.replace("/connection-error");
    }
  } else {
    Logger.error(`API error in ${context}`, error, ["ApiClient"]);
  }
};

/**
 * Check if error should trigger network error handling
 */
const isNetworkError = (error: unknown): error is AxiosError => {
  return (
    axios.isAxiosError(error) &&
    (error.code === "ERR_NETWORK" ||
      error.code === "NETWORK_ERROR" ||
      error.message === "Network Error" ||
      !error.response) // No response usually means network issue
  );
};

// Create an Axios instance with default configuration
const api = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  headers: {
    "Cache-Control": API_CONFIG.HEADERS.CACHE_CONTROL,
    Pragma: API_CONFIG.HEADERS.PRAGMA,
    "Content-Type": API_CONFIG.HEADERS.CONTENT_TYPE,
    "Client-Identifier": API_CONFIG.HEADERS.CLIENT_IDENTIFIER,
  },
  timeout: API_CONFIG.TIMEOUT,
});

// UNIFIED REQUEST INTERCEPTOR
api.interceptors.request.use(
  (request: InternalAxiosRequestConfig) => {
    // Get fresh token for each request
    const token = getAccessToken();

    // Only add Bearer token for external API calls
    if (request.baseURL !== window.location.origin && token) {
      request.headers.Authorization = `Bearer ${token}`;
      Logger.debug("Bearer token attached to request", ["ApiClient"]);
    } else if (!token && request.baseURL !== window.location.origin) {
      Logger.warn("No access token available for external request", [
        "ApiClient",
      ]);
    }

    Logger.info(
      `[API Request] ${request.method?.toUpperCase()} ${request.url}`,
      ["ApiClient"],
    );
    return request;
  },
  (error: AxiosError) => {
    Logger.error("[API Request Error]", error.message, ["ApiClient"]);

    if (isNetworkError(error) && !isLocalhost) {
      handleNetworkError(error, "Request Interceptor");
    }

    return Promise.reject(
      error instanceof Error ? error : new Error(String(error)),
    );
  },
);

// UNIFIED RESPONSE INTERCEPTOR
api.interceptors.response.use(
  (response: AxiosResponse) => {
    Logger.info(`[API Response] ${response.status} ${response.config.url}`, [
      "ApiClient",
    ]);
    return response;
  },
  async (error: unknown) => {
    const requestUrl = axios.isAxiosError(error)
      ? error.config?.url || "unknown"
      : "unknown";

    // Handle network errors first (highest priority)
    if (isNetworkError(error) && !isLocalhost) {
      handleNetworkError(error, `Response from ${requestUrl}`);
      return Promise.reject(
        error instanceof Error ? error : new Error(String(error)),
      );
    }

    // Handle 401 unauthorized
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      Logger.error("Received 401 Unauthorized - token may be expired", error, [
        "ApiClient",
      ]);
    }

    // Enhanced error logging for other errors
    if (axios.isAxiosError(error)) {
      if (error.response) {
        Logger.error(
          `[API Response Error] ${error.response.status} ${error.response.config.url}`,
          error.response.data,
          ["ApiClient"],
        );
      } else if (error.request) {
        Logger.error("[API No Response]", error.request, ["ApiClient"]);
      } else {
        Logger.error("[API Setup Error]", error.message, ["ApiClient"]);
      }
    } else {
      Logger.error("[Non-Axios Error]", error, ["ApiClient"]);
    }

    return Promise.reject(
      error instanceof Error ? error : new Error(String(error)),
    );
  },
);

// CRUD utility functions with axios.isAxiosError()
export const apiGet = async <T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> => {
  try {
    const response = await api.get<T>(url, config);
    return response.data;
  } catch (error) {
    Logger.error(`[GET Error] ${url}`, error, ["ApiClient"]);

    // Handle network errors in GET requests
    if (isNetworkError(error) && !isLocalhost) {
      handleNetworkError(error, `GET ${url}`);
    }

    throw error;
  }
};

export const apiPost = async <T>(
  url: string,
  data: unknown,
  config?: AxiosRequestConfig,
): Promise<T> => {
  try {
    const response = await api.post<T>(url, data, config);
    return response.data;
  } catch (error) {
    Logger.error(`[POST Error] ${url}`, error, ["ApiClient"]);

    // Handle network errors in POST requests
    if (isNetworkError(error) && !isLocalhost) {
      handleNetworkError(error, `POST ${url}`);
    }

    throw error;
  }
};

export const apiPut = async <T>(
  url: string,
  data: unknown,
  config?: AxiosRequestConfig,
): Promise<T> => {
  try {
    const response = await api.put<T>(url, data, config);
    return response.data;
  } catch (error) {
    Logger.error(`[PUT Error] ${url}`, error, ["ApiClient"]);

    // Handle network errors in PUT requests
    if (isNetworkError(error) && !isLocalhost) {
      handleNetworkError(error, `PUT ${url}`);
    }

    throw error;
  }
};

export const apiDelete = async <T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> => {
  try {
    const response = await api.delete<T>(url, config);
    return response.data;
  } catch (error) {
    Logger.error(`[DELETE Error] ${url}`, error, ["ApiClient"]);

    // Handle network errors in DELETE requests
    if (isNetworkError(error) && !isLocalhost) {
      handleNetworkError(error, `DELETE ${url}`);
    }

    throw error;
  }
};

// Export utility functions for manual error handling in components
export const handleApiError = (error: unknown, context: string): void => {
  if (isNetworkError(error) && !isLocalhost) {
    handleNetworkError(error, context);
  } else {
    Logger.error(`API error in ${context}`, error, ["ApiClient"]);
  }
};

export const isApiNetworkError = (error: unknown): error is AxiosError => {
  return isNetworkError(error);
};

// Additional utility functions for better error handling
export const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    if (
      error.response?.data &&
      typeof error.response.data === "object" &&
      "message" in error.response.data &&
      typeof error.response.data.message === "string"
    ) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return error.response.data.message;
    }
    if (error.message) {
      return error.message;
    }
    return `HTTP ${error.response?.status || "Unknown"} Error`;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "Unknown error occurred";
};

export const getErrorStatusCode = (error: unknown): number | null => {
  if (axios.isAxiosError(error)) {
    return error.response?.status || null;
  }
  return null;
};

export default api;
